/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Typography, Box, Badge, Avatar } from '@material-ui/core';
import { Notifications } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Button from '../../components/common/Button';
import useNotificationMenu, { notificationActions } from './useNotificationMenu';
import HandleInvitationDialog from './HandleInvitationDialog';
import { notificationType } from '../../shared/enums/NotificationType';

const useStyles = makeStyles(theme => ({
  menuIcon: {
    marginRight: theme.spacing(1),
    width: '30px',
    height: '30px',
  },
  spacer: {
    marginRight: theme.spacing(1),
  },
  notificationNav: {
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
  },
  notification: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  notificationBody: {
    display: 'flex',
    width: '100%',
  },
  large: {
    width: '3.25rem',
    height: '3.25rem',
    marginRight: theme.spacing(1),
  },
  userInitialsText: {
    fontSize: '1.25rem',
    fontWeight: '700',
    marginTop: theme.spacing(0.25),
  },
  avatarPrimary: {
    color: theme.palette.avatar.primary.text,
    backgroundColor: theme.palette.avatar.primary.background,
    position: 'relative',
  },
  avatarSecondary: {
    color: theme.palette.avatar.secondary.text,
    backgroundColor: theme.palette.avatar.secondary.background,
    position: 'relative',
  },
  notificationFooter: {
    display: 'flex',
    flex: '1',
    flexFlow: 'row wrap',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.border}`,
  },
  cancelButton: {
    marginRight: theme.spacing(1),
    marginLeft: '3.5rem',
  },
}));

export default function NotificationMenu({ notifications, userId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    anchorEl,
    dialog,
    invitation,
    invitationStatus,
    onToggleDialog,
    handleClose,
    handleClick,
    executeNotificationAction,
    onSubmit,
    onAcceptedInvitationConfirm,
  } = useNotificationMenu(userId);

  return (
    <>
      <Box className={clsx(classes.notificationNav)} role="toolbar" aria-label={t('aria.header.notification')}>
        <Button
          variant="text"
          id="alert"
          size="small"
          color="inherit"
          onClick={handleClick}
          aria-controls="simple-menu"
        >
          <Badge badgeContent={notifications?.length ?? 0} color="error">
            <Notifications fontSize="large" className={classes.menuIcon} />
          </Badge>
        </Button>

        {notifications?.length > 0 && (
          <Menu
            id="simple-menu"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            getContentAnchorEl={null}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            style={{ zIndex: '3000' }}
          >
            {notifications.map((notification) => {
              const initials = notification.invitationSender
                ?.match(/(\b\S)?/g)
                .join('')
                .match(/(^\S|\S$)?/g)
                .join('')
                .toUpperCase();

              // check notification type and render different content
              let notificationContent;
              let notificationFooter;

              switch (notification.type) {
                case notificationType.JOIN_ORGANIZATION:
                  notificationContent = (
                    <>
                      <Avatar className={clsx(classes.avatarPrimary, classes.large)}>
                        <Typography className={classes.userInitialsText}>{initials || 'DB'}</Typography>
                      </Avatar>
                      <p>
                        {t('user-management.pending.invite', {
                          user: notification.invitationSender,
                          organisationName: notification.organizationName,
                        })}
                      </p>
                    </>
                  );
                  notificationFooter = (
                    <>
                      <Button
                        className={classes.cancelButton}
                        onClick={() => executeNotificationAction(notification, notificationActions.DECLINE_INVITATION)}
                        color="default"
                      >
                        {t('actions.decline')}
                      </Button>
                      <Button
                        onClick={() => executeNotificationAction(notification, notificationActions.ACCEPT_INVITATION)}
                        color="primary"
                      >
                        {`${t('actions.confirm')}`}
                      </Button>
                    </>
                  );
                  break;

                case notificationType.MERGE_USER_ACCOUNT:
                  notificationContent = (
                    <>
                      <Avatar className={clsx(classes.avatarPrimary, classes.large)}>
                        <Typography className={classes.userInitialsText}>{initials || 'DB'}</Typography>
                      </Avatar>
                      <p>
                        {t('user-management.merge.pending.invite', {
                          user: notification.invitationSender,
                        })}
                      </p>
                    </>
                  );
                  notificationFooter = (
                    <>
                      <Button
                        className={classes.cancelButton}
                        onClick={() => executeNotificationAction(notification, notificationActions.DECLINE_INVITATION)}
                        color="default"
                      >
                        {t('actions.decline')}
                      </Button>
                      <Button
                        onClick={() => executeNotificationAction(notification, notificationActions.ACCEPT_INVITATION)}
                        color="primary"
                      >
                        {`${t('actions.confirm')}`}
                      </Button>
                    </>
                  );
                  break;

                default:
                  break;
              }
              return (
                <MenuItem key={notification.id} data-value={notification}>
                  <div className={classes.notification}>
                    <div className={classes.notificationBody}>{notificationContent}</div>

                    <div className={classes.notificationFooter}>{notificationFooter}</div>
                  </div>
                </MenuItem>
              );
            })}
          </Menu>
        )}
      </Box>

      {dialog.open && (
        <HandleInvitationDialog
          dialog={dialog}
          invitation={invitation}
          invitationStatus={invitationStatus}
          onSubmit={onSubmit}
          onAcceptedInvitationConfirm={onAcceptedInvitationConfirm}
          toggleDialog={onToggleDialog}
        />
      )}
    </>
  );
}
